import React, { useState, useEffect } from "react";
import { FiX, FiPlus, FiEye, FiUpload } from "react-icons/fi";
import { auth, db } from "../../firebase";
import { approvalService } from "../../services/firestoreService";
import { doc, getDoc } from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/contentnewpanel.css";

const ContentNewPanel = () => {
  const { getTranslations } = useLanguage();
  const t = getTranslations(false, true);
  const PUBLISH_CATEGORIES = [
    "Menstrual Wellness and Hygiene",
    "Contraception",
    "Fertility Basics",
    "STI Awareness",
    "Mental Health in Reproductive Care",
    "Male Reproductive Health",
    "Pregnancy Prevention",
    "General Reproductive Wellness",
    "Community Outreach Programs",
  ];

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState("");
  const [status, setStatus] = useState("draft");
  const [category, setCategory] = useState(PUBLISH_CATEGORIES[0]);
  const [minorEligible, setMinorEligible] = useState(false);
  const [curatorName, setCuratorName] = useState("");

  // Fetch user data to get curator's full name
  useEffect(() => {
    const fetchUserData = async (user) => {
      try {
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setCuratorName(data.fullName || "");
          }
        } else {
          // Reset curator name when user logs out
          setCuratorName("");
        }
      } catch (error) {
        console.error('ContentNewPanel: Error fetching user data:', error);
      }
    };

    // Use auth state listener to properly handle auth changes
    const unsubscribe = auth.onAuthStateChanged((user) => {
      fetchUserData(user);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const addTag = () => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      setTags([...tags, currentTag.trim()]);
      setCurrentTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handlePublish = async () => {
    try {
      if (!title.trim()) {
        alert("Please enter a title before submitting.");
        return;
      }

      const contentData = {
        title,
        description,
        tags,
        author: curatorName, // Use curator's full name as author
        authorId: auth.currentUser?.uid, // Add current user's ID for isolation
        category,
        minorEligible,
        socialTitle: title,
        socialDescription: description,
        socialImageUrl: "", // Can be added later
        bodyHtml: `<p>${description}</p>`, // Convert description to HTML
        contentId: `content_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` // Generate truly unique content ID
      };

      const result = await approvalService.submitForApproval(contentData);
      
      if (result) {
        alert("Content submitted for approval successfully!");
        // Reset form
        setTitle("");
        setDescription("");
        setTags([]);
        setCurrentTag("");
        setStatus("draft");
        setCategory(PUBLISH_CATEGORIES[0]);
        setMinorEligible(false);
      } else {
        alert("Failed to submit content for approval.");
      }
    } catch (error) {
      console.error("Error submitting content:", error);
      alert("Failed to submit content for approval.");
    }
  };

  return (
    <div className="cnp-container">
      <header className="cnp-header">
        <h2>Create New Content</h2>
      </header>

      <div className="cnp-form">
        <div className="cnp-field">
          <label>Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter content title"
            style={{ direction: "ltr" }}
          />
        </div>

        <div className="cnp-field">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Write detailed content here..."
            style={{ direction: "ltr" }}
            rows={10}
          />
        </div>

        <div className="cnp-field">
          <label>Tags</label>
          <div className="cnp-tags">
            {tags.map((tag, index) => (
              <span key={index} className="cnp-tag">
                {tag}
                <button onClick={() => removeTag(tag)}>
                  <FiX />
                </button>
              </span>
            ))}
            <div className="cnp-add-tag">
              <input
                type="text"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addTag()}
                placeholder="+ Add tag"
                style={{ direction: "ltr" }}
              />
              <button onClick={addTag}>
                <FiPlus />
              </button>
            </div>
          </div>
        </div>

        <div className="cnp-field">
          <label>Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {PUBLISH_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="cnp-field">
          <label>
            <input
              type="checkbox"
              checked={minorEligible}
              onChange={(e) => setMinorEligible(e.target.checked)}
              style={{ marginRight: "8px" }}
            />
            Appropriate for minors
          </label>
        </div>

        <div className="cnp-actions">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>

          <div className="cnp-buttons">
            <button className="cnp-btn secondary" onClick={() => {/* reset */}}>
              Cancel
            </button>
            <button className="cnp-btn primary" onClick={handlePublish}>
              <FiUpload /> Submit for Approval
            </button>
          </div>
        </div>
      </div>

      <div className="cnp-preview">
        <h3>Preview</h3>
        <div className="cnp-preview-content">
          <h1>{title || "Title"}</h1>
          <p>{description || "Description preview..."}</p>
          <div className="cnp-preview-tags">
            {tags.map((tag, index) => (
              <span key={index}>{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentNewPanel;

